import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import { selectGroupDetails } from "src/ui/containers/dashboard/devices/store";

import ActionsMenu from "@dashboard/files/components/ActionsMenu/ActionsMenu";
import { FileSettingsDialog } from "@dashboard/shared/components/FileSettingsDialog";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { FileInfo } from "@files/types";
import { hasAnalyzerInProgress, hasApkMetadata, hasFailedAnalyzer, isApk } from "@files/utils";
import { selectGroupById } from "@groups/store/selectors";
import { Box } from "@mui/material";
import { PermissionService } from "@services/permissionService/permissionService";
import { SecondaryButton } from "@shared/CustomButton";

import { useStyles } from "../FileTab.style";

import { PickInstallationTypeDialog } from "./PickInstallationTypeDialog";

type Props = {
    file: FileInfo;
    groupId: number;
};
const ActionsCell = (props: Props) => {
    const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);
    const [isInstallationTypeModalOpen, setIsInstallationTypeModalOpen] = React.useState(false);

    const { t } = useTranslation(["sidepanels"]);
    const { classes } = useStyles();
    const group = useSelector((state: RootState) => selectGroupById(state, props.groupId));
    const isApkFile = isApk(props.file.filename);
    const analyzerInProgress = hasAnalyzerInProgress(props.file);
    const analyzerFailed = hasFailedAnalyzer(props.file);
    const apkMetadataExist = hasApkMetadata(props.file);

    const isExpired = useCurrentSubscriptionExpired();

    const { data: workspace } = useCurrentWorkspace();
    const { groupAbility, fileAbility } = PermissionService();
    const mayManageTags = fileAbility(props.file).can("manage", "Tag");

    const getInstallButtonProps = () => {
        if (groupAbility(group || null, workspace).cannot("install", "App")) {
            return {
                tooltip: t("sidepanels:groupPanel.tooltip.installError"),
                iconClass: "fa-solid fa-arrow-right-to-bracket",
                disabled: true,
            };
        }
        if (isExpired) {
            return {
                tooltip: t("sidepanels:groupPanel.tooltip.subscriptionExpired"),
                iconClass: "fa-solid fa-arrow-right-to-bracket",
                disabled: true,
            };
        }
        if (analyzerInProgress) {
            return {
                tooltip: t("sidepanels:groupPanel.tooltip.processing"),
                iconClass: "fas fa-spinner fa-spin",
                disabled: true,
            };
        }
        if (analyzerFailed) {
            return {
                tooltip: t("sidepanels:groupPanel.tooltip.analysisFailed"),
                iconClass: "fa-solid fa-triangle-exclamation",
                disabled: true,
            };
        }
        if (!apkMetadataExist) {
            return {
                tooltip: t("sidepanels:groupPanel.tooltip.invalidPackage"),
                iconClass: "fa-solid fa-arrow-right-to-bracket",
                disabled: true,
            };
        } else {
            return {
                tooltip: t("sidepanels:groupPanel.tooltip.installToAllDevices"),
                iconClass: "fa-solid fa-arrow-right-to-bracket",
                disabled: false,
            };
        }
    };
    const getTagBtnTitle = () => {
        const result = { title: t("sidepanels:groupPanel.tooltip.manageTags") };

        if (isExpired) {
            result.title = t("sidepanels:groupPanel.tooltip.subscriptionExpired");
        }
        if (!mayManageTags) {
            result.title = t("sidepanels:groupPanel.tooltip.manageTagsError");
        }
        return result;
    };

    return (
        <React.Fragment>
            <Box className={classes.buttonBox}>
                {isApkFile && (
                    <SecondaryButton
                        onClick={() => setIsInstallationTypeModalOpen(true)}
                        tooltipProps={{ title: getInstallButtonProps().tooltip }}
                        disabled={getInstallButtonProps().disabled}
                    >
                        <i className={getInstallButtonProps().iconClass} />
                    </SecondaryButton>
                )}
                <SecondaryButton
                    onClick={() => setIsSettingsModalOpen(true)}
                    tooltipProps={getTagBtnTitle()}
                    disabled={!mayManageTags}
                >
                    <i className="fa-solid fa-tags" />
                </SecondaryButton>
                <ActionsMenu file={props.file} />
            </Box>

            {group ? (
                <PickInstallationTypeDialog
                    file={props.file}
                    group={group}
                    open={isInstallationTypeModalOpen}
                    onClose={() => setIsInstallationTypeModalOpen(false)}
                />
            ) : null}
            <FileSettingsDialog item={props.file} open={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} />
        </React.Fragment>
    );
};

export default ({ file }: { file: FileInfo }) => {
    const details = useSelector(selectGroupDetails);

    if (!details?.selectedId) {
        return null;
    }

    return <ActionsCell {...{ file, groupId: details.selectedId }} />;
};
